import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
  Checkbox,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  ContentCut as ContentCutIcon,
  Search as SearchIcon,
  FolderOpen as FolderOpenIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { surface } from '../theme';
import VideoFootageService from '../services/VideoFootageService';
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';
import FootageDeleteConfirmDialog from './video-footage/FootageDeleteConfirmDialog';
import MobileListCard from '../components/responsive/MobileListCard';
import FootageFolderTagsSelect, {
  footageFolderTagLabel,
} from '../components/FootageFolderTagsSelect';

const PAGE_SIZE = 10;

function formatOwnerLabel(u) {
  if (!u) return '';
  const name = u.displayName || u.username || '';
  const id = u.id || u._id;
  return id ? `${name} (${String(id).slice(-6)})` : name;
}

/** Total folder size from API (bytes). */
function formatTotalSizeBytes(totalSizeBytes, videoCount) {
  if (videoCount == null || videoCount === 0) {
    return '—';
  }
  const n = Number(totalSizeBytes) || 0;
  const mb = n / (1024 * 1024);
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(2)} GB`;
  }
  return `${mb.toFixed(2)} MB`;
}

function formatApiError(err, fallback) {
  const data = err.response?.data;
  const message = data?.error || err.message || fallback;
  if (data?.current && Array.isArray(data?.required)) {
    return `${message} Current role: ${data.current}. Required: ${data.required.join(', ')}.`;
  }
  return message;
}

export default function VideoFootagePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const currentUser = AuthService.getUser();
  const currentUserId = currentUser?.id || currentUser?._id;
  const isSuperadmin = AuthService.hasRole('superadmin');
  const canCreateFolder = AuthService.hasRole(['uploader', 'admin', 'superadmin']);
  const canManageFootage = AuthService.hasRole(['admin', 'superadmin']);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [searchTitle, setSearchTitle] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const searchDebounceRef = useRef(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createTags, setCreateTags] = useState([]);
  const [createOwners, setCreateOwners] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [editOwners, setEditOwners] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [mobileMenuItem, setMobileMenuItem] = useState(null);

  const loadItems = useCallback(async (p, params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await VideoFootageService.listFolders({ page: p + 1, ...params });
      setItems(data.items || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load folders'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems(page, { search: searchDebounced?.trim() || undefined });
  }, [page, searchDebounced, loadItems]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchDebounced(searchTitle?.trim() ?? '');
      setPage(0);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTitle]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, searchDebounced]);

  const normalizeUsers = (users) =>
    (users || []).map((u) => ({
      id: u.id || u._id?.toString(),
      username: u.username,
      displayName: u.displayName,
      role: u.role,
    }));

  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const users = await UserService.getUsers();
      const normalized = normalizeUsers(users);
      setAllUsers(normalized);
      return normalized;
    } catch {
      setAllUsers([]);
      return [];
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const handleOpenCreate = async () => {
    setCreateName('');
    setCreateTags([]);
    setCreateError(null);
    setCreateOpen(true);
    if (isSuperadmin) {
      const normalized = await loadUsers();
      const self = normalized.find((u) => String(u.id) === String(currentUserId));
      setCreateOwners(self ? [self] : []);
    } else {
      setCreateOwners([]);
    }
  };

  const handleCreate = async () => {
    const name = createName?.trim();
    if (!name) {
      setCreateError('Name is required');
      return;
    }
    if (isSuperadmin && !createOwners.length) {
      setCreateError('At least one owner is required');
      return;
    }
    try {
      setCreating(true);
      setCreateError(null);
      await VideoFootageService.createFolder({
        name,
        tags: createTags,
        ...(isSuperadmin ? { ownerIds: createOwners.map((o) => o.id) } : {}),
      });
      setCreateOpen(false);
      loadItems(page, { search: searchDebounced?.trim() || undefined });
    } catch (err) {
      setCreateError(formatApiError(err, 'Failed to create folder'));
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEdit = async (item) => {
    setEditItem(item);
    setEditName(item.name);
    setEditTags(item.tags || []);
    setEditError(null);
    const mapped = (item.owners || []).map((o) => ({
      id: o.id,
      username: o.username,
      displayName: o.displayName,
    }));
    setEditOwners(mapped);
    if (isSuperadmin) {
      const normalized = await loadUsers();
      const byId = new Map(normalized.map((u) => [u.id, u]));
      setEditOwners(mapped.map((o) => byId.get(o.id) || o));
    }
  };

  const handleSaveEdit = async () => {
    const name = editName?.trim();
    if (!name) {
      setEditError('Name is required');
      return;
    }
    if (isSuperadmin && !editOwners.length) {
      setEditError('At least one owner is required');
      return;
    }
    if (!editItem) return;
    try {
      setSavingEdit(true);
      setEditError(null);
      await VideoFootageService.updateFolder(editItem.id, {
        name,
        tags: editTags,
        ...(isSuperadmin ? { ownerIds: editOwners.map((o) => o.id) } : {}),
      });
      setEditItem(null);
      loadItems(page, { search: searchDebounced?.trim() || undefined });
    } catch (err) {
      setEditError(formatApiError(err, 'Failed to update'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteClick = (item, e) => {
    e.stopPropagation();
    if (selectedIds.size > 0 && selectedIds.has(item.id)) {
      const payloadItems = items
        .filter((i) => selectedIds.has(i.id))
        .map((i) => ({
          id: i.id,
          name: i.name,
          videoCount: i.videoCount ?? 0,
          totalSizeBytes: i.totalSizeBytes ?? 0,
        }));
      setDeleteItem({ items: payloadItems });
    } else {
      setDeleteItem({
        id: item.id,
        name: item.name,
        videoCount: item.videoCount ?? 0,
        totalSizeBytes: item.totalSizeBytes ?? 0,
      });
    }
  };

  const handleDeleteClose = () => {
    if (!deleting) setDeleteItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    try {
      setDeleting(true);
      if (deleteItem.items) {
        const results = await Promise.allSettled(
          deleteItem.items.map((it) => VideoFootageService.deleteFolder(it.id)),
        );
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          const first = failed[0].reason;
          const msg = first?.response?.data?.error || first?.message || 'Failed to delete';
          setError(
            failed.length === deleteItem.items.length
              ? msg
              : `${deleteItem.items.length - failed.length} deleted; ${failed.length} failed: ${msg}`,
          );
        }
      } else {
        await VideoFootageService.deleteFolder(deleteItem.id);
      }
      setDeleteItem(null);
      setSelectedIds(new Set());
      loadItems(page, { search: searchDebounced?.trim() || undefined });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };
  const openMobileMenu = (event, item) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
    setMobileMenuItem(item);
  };
  const closeMobileMenu = () => {
    setMobileMenuAnchorEl(null);
    setMobileMenuItem(null);
  };

  if (!AuthService.hasRole(['uploader', 'admin', 'superadmin'])) {
    return <Navigate to='/' replace />;
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box>
      <Typography
        variant='h4'
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 3,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        Video Footage
      </Typography>

      <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {canCreateFolder && (
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 2, width: { xs: '100%', sm: 'auto' } }}
              >
                New folder
              </Button>
            )}
            {canManageFootage && (
              <Button
                variant='outlined'
                startIcon={<ContentCutIcon />}
                onClick={() => navigate('/video-footage/cut')}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
              >
                Cut footage
              </Button>
            )}
            <TextField
              size='small'
              placeholder='Search by name'
              value={searchTitle}
              onChange={(e) => setSearchTitle(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 0, width: { xs: '100%', sm: 240 } }}
            />
          </Box>

          {loading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography color='text.secondary' sx={{ py: 3 }}>
              No folders yet. Create a folder to upload footage.
            </Typography>
          ) : isMobile ? (
            <>
              {items.map((item) => (
                <MobileListCard
                  key={item.id}
                  title={item.name}
                  subtitle={`Videos: ${item.videoCount ?? 0} • ${formatTotalSizeBytes(item.totalSizeBytes, item.videoCount)}`}
                  meta={item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                  onClick={() => navigate(`/video-footage/${item.id}`)}
                  primaryAction={
                    <Button size='small' onClick={() => navigate(`/video-footage/${item.id}`)} sx={{ minWidth: 0, px: 1 }}>
                      Open
                    </Button>
                  }
                  secondaryAction={
                    canManageFootage ? (
                      <IconButton size='small' onClick={(e) => openMobileMenu(e, item)}>
                        <MoreVertIcon fontSize='small' />
                      </IconButton>
                    ) : null
                  }
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(item.tags || []).length ? (
                      item.tags.map((tag) => (
                        <Chip key={tag} size='small' label={footageFolderTagLabel(tag)} />
                      ))
                    ) : (
                      <Chip size='small' label='None' variant='outlined' />
                    )}
                  </Box>
                  {isSuperadmin && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(item.owners || []).map((o) => (
                        <Chip key={o.id} size='small' label={o.displayName || o.username || o.id} />
                      ))}
                    </Box>
                  )}
                </MobileListCard>
              ))}
              {canManageFootage && (
                <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={closeMobileMenu}>
                <MenuItem
                  onClick={() => {
                    if (mobileMenuItem) navigate(`/video-footage/${mobileMenuItem.id}`);
                    closeMobileMenu();
                  }}
                >
                  Open folder
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (!mobileMenuItem) return;
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(mobileMenuItem.id)) next.delete(mobileMenuItem.id);
                      else next.add(mobileMenuItem.id);
                      return next;
                    });
                    closeMobileMenu();
                  }}
                >
                  Toggle selection
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (mobileMenuItem) handleOpenEdit(mobileMenuItem);
                    closeMobileMenu();
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  onClick={(e) => {
                    if (mobileMenuItem) handleDeleteClick(mobileMenuItem, e);
                    closeMobileMenu();
                  }}
                >
                  Delete
                </MenuItem>
                </Menu>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Pagination
                  count={pageCount}
                  page={page + 1}
                  onChange={(_, value) => setPage(value - 1)}
                  color='primary'
                  size='small'
                />
              </Box>
            </>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {canManageFootage && (
                        <TableCell padding='checkbox' sx={{ fontWeight: 700 }}>
                          <Checkbox
                            size='small'
                            indeterminate={
                              items.some((i) => selectedIds.has(i.id)) &&
                              !items.every((i) => selectedIds.has(i.id))
                            }
                            checked={items.length > 0 && items.every((i) => selectedIds.has(i.id))}
                            onChange={() => {
                              const ids = items.map((i) => i.id);
                              setSelectedIds((prev) => {
                                const allSelected =
                                  ids.length > 0 && ids.every((id) => prev.has(id));
                                const next = new Set(prev);
                                if (allSelected) {
                                  ids.forEach((id) => next.delete(id));
                                } else {
                                  ids.forEach((id) => next.add(id));
                                }
                                return next;
                              });
                            }}
                            inputProps={{ 'aria-label': 'Select all on this page' }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Used for</TableCell>
                      {isSuperadmin && (
                        <TableCell sx={{ fontWeight: 700 }}>Owners</TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700 }} align='right'>
                        Videos
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align='right'>
                        Total size
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                      {canManageFootage && (
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/video-footage/${item.id}`)}
                      >
                        {canManageFootage && (
                          <TableCell padding='checkbox' onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size='small'
                              checked={selectedIds.has(item.id)}
                              onChange={() => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(item.id)) next.delete(item.id);
                                  else next.add(item.id);
                                  return next;
                                });
                              }}
                              inputProps={{ 'aria-label': `Select folder ${item.name || ''}` }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FolderOpenIcon fontSize='small' color='action' />
                            {item.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(item.tags || []).length ? (
                              item.tags.map((tag) => (
                                <Chip
                                  key={tag}
                                  size='small'
                                  label={footageFolderTagLabel(tag)}
                                />
                              ))
                            ) : (
                              <Chip size='small' label='None' variant='outlined' />
                            )}
                          </Box>
                        </TableCell>
                        {isSuperadmin && (
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(item.owners || []).map((o) => (
                                <Chip
                                  key={o.id}
                                  size='small'
                                  label={o.displayName || o.username || o.id}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align='right'>{item.videoCount ?? 0}</TableCell>
                        <TableCell align='right'>
                          {formatTotalSizeBytes(item.totalSizeBytes, item.videoCount)}
                        </TableCell>
                        <TableCell>
                          {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                        </TableCell>
                        {canManageFootage && (
                          <TableCell sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                            {selectedIds.size > 0 && selectedIds.has(item.id) ? (
                              <Tooltip
                                title={
                                  selectedIds.size > 1
                                    ? `Delete ${selectedIds.size} selected folders`
                                    : 'Delete'
                                }
                              >
                                <IconButton
                                  size='small'
                                  color='error'
                                  onClick={(e) => handleDeleteClick(item, e)}
                                >
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title='Edit folder'>
                                  <IconButton size='small' onClick={() => handleOpenEdit(item)}>
                                    <EditIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title='Delete folder'>
                                  <IconButton
                                    size='small'
                                    color='error'
                                    onClick={(e) => handleDeleteClick(item, e)}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  {total === 0 ? 0 : page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of{' '}
                  {total}
                </Typography>
                <Pagination
                  count={pageCount}
                  page={page + 1}
                  onChange={(_, value) => setPage(value - 1)}
                  color='primary'
                  size='small'
                  siblingCount={2}
                  boundaryCount={1}
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin='dense'
            label='Folder name'
            fullWidth
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            sx={{ mb: 1 }}
          />
          <FootageFolderTagsSelect
            value={createTags}
            onChange={setCreateTags}
            disabled={creating}
            sx={{ mb: isSuperadmin ? 1 : 0 }}
          />
          {isSuperadmin && (
            <Autocomplete
              multiple
              options={allUsers}
              loading={usersLoading}
              getOptionLabel={(o) => formatOwnerLabel(o)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={createOwners}
              onChange={(_, v) => setCreateOwners(v)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={formatOwnerLabel(option)} {...getTagProps({ index })} key={option.id} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label='Owners' placeholder='Select users' />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={22} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editItem}
        onClose={() => !savingEdit && setEditItem(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Edit folder</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <TextField
            margin='dense'
            label='Folder name'
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 1 }}
          />
          <FootageFolderTagsSelect
            value={editTags}
            onChange={setEditTags}
            disabled={savingEdit}
            sx={{ mb: isSuperadmin ? 1 : 0 }}
          />
          {isSuperadmin && (
            <Autocomplete
              multiple
              options={allUsers}
              loading={usersLoading}
              getOptionLabel={(o) => formatOwnerLabel(o)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={editOwners}
              onChange={(_, v) => setEditOwners(v)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={formatOwnerLabel(option)} {...getTagProps({ index })} key={option.id} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label='Owners' placeholder='Select users' />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)} disabled={savingEdit}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <FootageDeleteConfirmDialog
        mode='folders'
        deleteItem={deleteItem}
        deleting={deleting}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
